(() => {
  'use strict';

  // Store-wide: the section is rendered from the footer group on every page, so
  // the layer is bound to the THEME alone. A product page may ALSO still carry
  // the section in its own template; the script then loads twice and the second
  // boot must be a no-op, or every decoration would run against itself.
  if (window.__olivineCartV2Booted) return;
  window.__olivineCartV2Booted = true;

  // Hero identity (handle, variant ids, path, offer) is whatever the page's own
  // product rendered into the config. It is EMPTY on every non-product page and
  // only ever drives the q1/q2 offer strip; nothing else depends on it.
  const EXPECTED = Object.freeze({
    themeId: '140766347351',
    themeRole: 'main'
  });
  const host = document.getElementById('olivine-cart-v2-config');
  if (!host || host.dataset.cartV2Guard !== 'olivine-140766347351') return;

  let config;
  try {
    config = JSON.parse(host.querySelector('script[type="application/json"]')?.textContent || '');
  } catch (_) {
    return;
  }

  const HERO_HANDLE = String(config.productHandle || '');
  const HERO_VARIANT_IDS = new Set((Array.isArray(config.variantIds) ? config.variantIds : []).map(String));
  const HERO_PATH_SUFFIX = `/products/${HERO_HANDLE}`;
  // "Add a second one and it's free!" is only true for a {qty:2,free:1} tier. The
  // section reads the hero's own quantity_break_tiers; a config without the key
  // (the pre-store-wide section) is the old product-template contract, where the
  // template itself was the promise, so it keeps the strip.
  const HERO_OFFER = config.heroOffer === undefined ? true : Boolean(config.heroOffer);

  function normalizedPath() {
    return window.location.pathname.replace(/\/+$/, '') || '/';
  }

  // The layer is on whenever the page is served by the main theme. No page type,
  // no product, no path: the drawer is the same drawer on every page.
  function activeContext() {
    const browserTheme = window.Shopify && window.Shopify.theme;
    const browserThemeId = String((browserTheme && browserTheme.id) || config.themeId || '');
    const browserThemeRole = String((browserTheme && browserTheme.role) || config.themeRole || '').toLowerCase();
    return browserThemeId === EXPECTED.themeId &&
      browserThemeRole === EXPECTED.themeRole &&
      String(config.themeId) === EXPECTED.themeId &&
      String(config.themeRole).toLowerCase() === EXPECTED.themeRole;
  }

  // The hero offer strip needs a hero: a product page, whose product has the
  // 2-for-1 tier, viewed at its own URL. A product is also reached at
  // /collections/<x>/products/<handle>, so the path is matched by its tail,
  // never by equality. Off every other page the strip simply does not exist.
  function heroContext() {
    return activeContext() &&
      HERO_OFFER &&
      HERO_HANDLE !== '' &&
      HERO_VARIANT_IDS.size > 0 &&
      normalizedPath().endsWith(HERO_PATH_SUFFIX);
  }

  if (!activeContext()) return;

  const html = document.documentElement;
  const status = document.getElementById('olivine-cart-v2-status');
  const OFFER_COPY = 'Add a second one and it’s free!';
  const SAVINGS_APPLIED = 'SAVINGS APPLIED';
  const ROUTINE_HEADING = 'Complete the Routine';
  const originalBadges = new WeakMap();
  let scheduled = false;
  let decorating = false;
  let rowPositions = new Map();
  let lastScroller = null;
  let cartScrollTop = 0;

  function announce(message, timeout = 4200) {
    if (!status || !activeContext()) return;
    status.textContent = message;
    status.hidden = false;
    window.clearTimeout(announce.timer);
    if (timeout) announce.timer = window.setTimeout(() => { status.hidden = true; }, timeout);
  }

  function greenRows(drawer) {
    if (!heroContext()) return [];
    return Array.from(drawer.querySelectorAll('.cart-item[data-variant-id]'))
      .filter((row) => HERO_VARIANT_IDS.has(String(row.dataset.variantId)));
  }

  function renderedQuantity(row) {
    const input = row && row.querySelector('quantity-input .quantity__input, quantity-input input[name="updates[]"]');
    const rendered = input && input.getAttribute('value');
    const quantity = Number.parseInt(rendered || row?.dataset.quantity || '', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  function confirmedGreenQuantity(drawer, event) {
    if (!heroContext()) return 0;
    const items = event?.detail?.parsedState?.items;
    if (Array.isArray(items)) {
      const quantity = items.reduce((sum, item) => HERO_VARIANT_IDS.has(String(item.id))
        ? sum + (Number.parseInt(item.quantity, 10) || 0)
        : sum, 0);
      if (quantity > 0 || !greenRows(drawer).length) return quantity;
    }
    return greenRows(drawer).reduce((sum, row) => sum + renderedQuantity(row), 0);
  }

  function cartItemCount(drawer) {
    const count = Number.parseInt(drawer.querySelector('.drawer__heading .gb-cart-total-item')?.textContent || '', 10);
    if (Number.isFinite(count)) return count;
    return Array.from(drawer.querySelectorAll('.cart-item quantity-input .quantity__input')).reduce((sum, input) => {
      const quantity = Number.parseInt(input.getAttribute('value') || '', 10);
      return sum + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
  }

  function updateNativeCopy(drawer) {
    const heading = drawer.querySelector('.drawer__heading');
    const totalRows = Array.from(drawer.querySelectorAll('.drawer__footer .gb-discounts-cart-values'));
    const totalRow = totalRows.find((row) => row.querySelector('.totals__total-value'));
    const totalLabel = totalRow?.querySelector('.totals__total');
    const checkoutLabel = drawer.querySelector('#CartDrawer-Checkout .button-text, .custom-checkout .button-text');
    if (heading) {
      if (heading.dataset.greenReviewOriginalHtml === undefined) heading.dataset.greenReviewOriginalHtml = heading.innerHTML;
      const count = cartItemCount(drawer);
      if (count > 0) {
        const countNode = heading.querySelector('.gb-cart-total-item') || document.createElement('span');
        countNode.className = 'gb-cart-total-item';
        countNode.textContent = String(count);
        heading.replaceChildren(document.createTextNode('Your Cart has '), countNode, document.createTextNode(` ${count === 1 ? 'item' : 'items'}`));
      }
    }
    if (totalLabel) {
      if (totalLabel.dataset.greenReviewOriginalHtml === undefined) totalLabel.dataset.greenReviewOriginalHtml = totalLabel.innerHTML;
      totalLabel.textContent = 'Estimated total';
    }
    if (checkoutLabel) {
      if (checkoutLabel.dataset.greenReviewOriginalText === undefined) checkoutLabel.dataset.greenReviewOriginalText = checkoutLabel.textContent;
      checkoutLabel.textContent = 'CONTINUE TO CHECKOUT';
    }
  }

  function restoreNativeCopy(root = document) {
    root.querySelectorAll('[data-green-review-original-html]').forEach((node) => {
      if (!node.matches('.drawer__heading, .totals__total')) return;
      node.innerHTML = node.dataset.greenReviewOriginalHtml;
      delete node.dataset.greenReviewOriginalHtml;
    });
    root.querySelectorAll('[data-green-review-original-text]').forEach((node) => {
      node.textContent = node.dataset.greenReviewOriginalText;
      delete node.dataset.greenReviewOriginalText;
    });
  }


  // The saved amount is the theme's own computed figure, read off the row. If
  // the theme does not render one, the strip is not built — never a number we
  // made up.
  function rowSaving(row) {
    const text = row.querySelector('.gb-price-drawer-saving')?.textContent || '';
    return text.match(/\$\s?\d[\d,]*(?:\.\d{2})?/)?.[0].replace(/\s+/g, '') || '';
  }

  // The badge text is the theme's own discount label on the row (the native
  // discount tag). It is re-homed into the strip, never written by us.
  function rowBadgeText(row) {
    const label = row.querySelector('.cart-item__discount-allocations .discount-label, .cart-item__discount-allocations .discount-tag');
    return (label?.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function tagIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20.6 13.6 11 23.2a2.7 2.7 0 0 1-3.8 0L.8 16.8a2.7 2.7 0 0 1 0-3.8L10.4 3.4c.5-.5 1.2-.8 1.9-.8h7.6c.8 0 1.5.7 1.5 1.5v7.6c0 .7-.3 1.4-.8 1.9ZM16.5 6.2a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4Z');
    svg.append(path);
    return svg;
  }

  // Saved strip: the deal badge (theme's label) on the left, the saving on the
  // right — one statement of the deal per tile, at the bottom. Offer strip at
  // q1 is unchanged.
  function buildItemOfferStrip(saved, badge) {
    const strip = document.createElement('td');
    strip.className = saved ? 'green-review-item-offer green-review-item-offer--saved' : 'green-review-item-offer';
    strip.colSpan = 2;
    strip.setAttribute('role', 'cell');
    const copy = document.createElement('span');
    copy.className = 'green-review-strip-copy';
    copy.textContent = saved ? `${SAVINGS_APPLIED} | ${saved} SAVED` : OFFER_COPY;
    if (saved && badge) {
      strip.classList.add('green-review-item-offer--badged');
      const pill = document.createElement('span');
      pill.className = 'green-review-strip-badge';
      const text = document.createElement('span');
      text.textContent = badge;
      pill.append(tagIcon(), text);
      strip.append(pill, copy);
    } else {
      strip.append(tagIcon(), copy);
    }
    return strip;
  }

  // One place appends a strip, so the row classes the CSS keys off can never
  // drift from the strip that is actually in the DOM.
  function attachItemOfferStrip(row, saved) {
    row.append(buildItemOfferStrip(saved, saved ? rowBadgeText(row) : ''));
    row.classList.add('green-review-stripped');
    if (saved) row.classList.add('green-review-saved');
  }

  function itemKey(row, occurrences) {
    const id = String(row.dataset.variantId || 'unknown');
    const index = occurrences.get(id) || 0;
    occurrences.set(id, index + 1);
    return `${id}:${index}`;
  }

  function animateRows(drawer) {
    const scroller = drawer.querySelector('#CartDrawer-CartItems');
    if (scroller && scroller !== lastScroller) scroller.scrollTop = cartScrollTop;
    lastScroller = scroller;
    if (scroller) cartScrollTop = scroller.scrollTop;
    const next = new Map();
    const occurrences = new Map();
    const canAnimate = drawer.classList.contains('active') && rowPositions.size > 0 &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    drawer.querySelectorAll('.cart-item[data-variant-id]').forEach((row) => {
      const key = itemKey(row, occurrences);
      const running = typeof row.getAnimations === 'function'
        ? row.getAnimations().find((animation) => animation.playState === 'running')
        : null;
      const previous = rowPositions.get(key);
      if (running && previous && previous.node === row) {
        next.set(key, previous);
        return;
      }
      const top = row.getBoundingClientRect().top;
      next.set(key, { node: row, top });
      if (canAnimate && typeof row.animate === 'function' && (!previous || Math.abs(previous.top - top) > 1)) {
        const frames = previous === undefined
          ? [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }]
          : [{ transform: `translateY(${previous.top - top}px)` }, { transform: 'translateY(0)' }];
        row.animate(frames, { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
      }
    });
    rowPositions = next;
  }

  function moveBadgesBelowDetails(drawer) {
    drawer.querySelectorAll('.cart-item__discount-allocations').forEach((badges) => {
      const target = badges.closest('.cart-item')?.querySelector('.cart-items-wrapper');
      if (!target || badges.parentElement === target) return;
      originalBadges.set(badges, { parent: badges.parentNode, next: badges.nextSibling });
      badges.classList.add('green-review-inline-badges');
      target.append(badges);
    });
  }

  function restoreBadges(root = document) {
    root.querySelectorAll('.green-review-inline-badges').forEach((badges) => {
      const original = originalBadges.get(badges);
      if (original && original.parent && original.parent.isConnected) {
        original.parent.insertBefore(badges, original.next && original.next.parentNode === original.parent ? original.next : null);
      }
      badges.classList.remove('green-review-inline-badges');
    });
  }

  function updateDots(track) {
    const dots = track.parentElement?.querySelector('.green-review-dots');
    const cards = Array.from(track.children);
    if (!dots || !cards.length) return;
    const nearest = cards.reduce((best, card, index) => {
      const distance = Math.abs(card.offsetLeft - track.scrollLeft);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Infinity }).index;
    dots.querySelectorAll('button').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === nearest);
      if (index === nearest) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function syncDots(upsell, track) {
    const cards = Array.from(track.children);
    let dots = upsell.querySelector(':scope > .green-review-dots');
    if (!dots || Number.parseInt(dots.dataset.cardCount || '', 10) !== cards.length) {
      dots?.remove();
      dots = document.createElement('div');
      dots.className = 'green-review-dots';
      dots.dataset.cardCount = String(cards.length);
      dots.setAttribute('aria-label', 'Offer pages');
      cards.forEach((_, cardIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show offer ${cardIndex + 1}`);
        dot.addEventListener('click', () => {
          if (!activeContext()) return;
          track.children[cardIndex]?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'start'
          });
        });
        dots.append(dot);
      });
      upsell.append(dots);
    }
    if (track.dataset.greenReviewScrollBound !== 'true') {
      track.dataset.greenReviewScrollBound = 'true';
      let frame = 0;
      track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => updateDots(track));
      }, { passive: true });
    }
    updateDots(track);
  }

  function carouselUpsells(drawer) {
    drawer.querySelectorAll('.cart-dynamic-upsell').forEach((upsell, index) => {
      if (index > 0) return;
      let track = upsell.querySelector(':scope > .green-review-upsell-track');
      if (!track) {
        const cards = Array.from(upsell.querySelectorAll(':scope > .upsell-product-container'));
        if (!cards.length) return;
        track = document.createElement('div');
        track.className = 'green-review-upsell-track';
        track.tabIndex = 0;
        track.setAttribute('aria-label', 'Complete the Routine offers');
        cards.forEach((card) => track.append(card));
        upsell.append(track);
      }
      upsell.classList.add('green-review-upsells');
      track.querySelectorAll('button[name="upsell-add"]').forEach((button) => {
        if (button.dataset.greenReviewOriginalHtml === undefined) {
          button.dataset.greenReviewOriginalHtml = button.innerHTML;
          button.dataset.greenReviewOriginalAria = button.getAttribute('aria-label') || '';
        }
        const label = button.querySelector('[class^="upsell-button-text-"], [class*=" upsell-button-text-"]');
        if (label) label.textContent = 'ADD';
        else button.textContent = 'ADD';
        const title = button.closest('.upsell-product-container')?.querySelector('.upsell-product-title, [class^="upsell-product-title-"], [class*=" upsell-product-title-"]')?.textContent.trim();
        if (title) button.setAttribute('aria-label', `Add ${title}`);
      });
      syncDots(upsell, track);
    });
  }

  function syncOfferPresentation(drawer, event) {
    const quantity = confirmedGreenQuantity(drawer, event);
    html.classList.toggle('green-balm-review-one', quantity === 1);
    html.classList.toggle('green-balm-review-complete', quantity === 2);
    drawer.querySelectorAll('.green-review-announcement, .green-review-item-offer').forEach((node) => node.remove());
    drawer.querySelectorAll('.green-review-green-item').forEach((row) => row.classList.remove('green-review-green-item'));
    drawer.querySelectorAll('.green-review-stripped, .green-review-saved').forEach((row) => row.classList.remove('green-review-stripped', 'green-review-saved'));

    const rows = greenRows(drawer);
    rows.forEach((row) => row.classList.add('green-review-green-item'));
    // The strip stays at two units, carrying the savings instead of the offer,
    // so the tile keeps one shape across both states.
    if (quantity === 1) rows.forEach((row) => attachItemOfferStrip(row, ''));
    if (quantity === 2) {
      rows.forEach((row) => {
        const saved = rowSaving(row);
        if (saved) attachItemOfferStrip(row, saved);
      });
    }
    // Every other line the theme already discounted carries the same strip, so
    // a paired free item reads as a saving instead of a bare "Free". The Green
    // row is handled above and skipped here; the figure is the theme's own.
    drawer.querySelectorAll('.cart-item[data-variant-id]').forEach((row) => {
      if (row.querySelector(':scope > .green-review-item-offer')) return;
      const saved = rowSaving(row);
      if (saved) attachItemOfferStrip(row, saved);
    });

    let upsell = drawer.querySelector('.green-review-upsells');
    let track = upsell?.querySelector(':scope > .green-review-upsell-track');
    if (quantity === 1 && (!upsell || !track)) {
      // The free Green Balm is claimed with the row's own plus button, so the
      // carousel is paid offers only. With no native container there is nothing
      // to carousel and nothing to invent.
      upsell = drawer.querySelector('.cart-dynamic-upsell');
      if (!upsell) return quantity;
      upsell.classList.add('green-review-upsells');
      let heading = upsell.querySelector(':scope > .cart-upsell-section-header');
      if (!heading) {
        heading = document.createElement('div');
        heading.className = 'cart-upsell-section-header green-review-created-heading';
        heading.textContent = ROUTINE_HEADING;
        upsell.prepend(heading);
      }
      track = document.createElement('div');
      track.className = 'green-review-upsell-track green-review-created-track';
      track.tabIndex = 0;
      heading.insertAdjacentElement('afterend', track);
    }
    if (!upsell || !track) return quantity;
    // The carousel never carries the free Green Balm. The banner and the row
    // strip announce the offer; the row's plus button claims it. Anything a
    // previous version left behind is swept here.
    const offerHeading = upsell.querySelector(':scope > .cart-upsell-section-header');
    track.querySelector(':scope > .green-review-free-card')?.remove();
    upsell.querySelector(':scope > .green-review-offer-eligibility')?.remove();
    if (offerHeading?.dataset.greenReviewOriginalText !== undefined) {
      offerHeading.textContent = offerHeading.dataset.greenReviewOriginalText;
      delete offerHeading.dataset.greenReviewOriginalText;
    }
    track.setAttribute('aria-label', 'Paid cart offers');
    syncDots(upsell, track);
    if (quantity !== 1 && track.classList.contains('green-review-created-track') && !track.children.length) {
      upsell.querySelector(':scope > .green-review-dots')?.remove();
      upsell.querySelector(':scope > .green-review-created-heading')?.remove();
      track.remove();
      upsell.classList.remove('green-review-upsells');
    }
    return quantity;
  }

  function restoreUpsells(root = document) {
    root.querySelectorAll('.green-review-upsells').forEach((upsell) => {
      const track = upsell.querySelector(':scope > .green-review-upsell-track');
      if (track) {
        track.querySelector(':scope > .green-review-free-card')?.remove();
        track.querySelectorAll('button[name="upsell-add"][data-green-review-original-html]').forEach((button) => {
          button.innerHTML = button.dataset.greenReviewOriginalHtml;
          if (button.dataset.greenReviewOriginalAria) button.setAttribute('aria-label', button.dataset.greenReviewOriginalAria);
          else button.removeAttribute('aria-label');
          delete button.dataset.greenReviewOriginalHtml;
          delete button.dataset.greenReviewOriginalAria;
        });
        Array.from(track.children).forEach((card) => upsell.insertBefore(card, track));
        track.remove();
      }
      upsell.querySelector(':scope > .green-review-offer-eligibility')?.remove();
      const offerHeading = upsell.querySelector(':scope > .cart-upsell-section-header[data-green-review-original-text]');
      if (offerHeading) {
        offerHeading.textContent = offerHeading.dataset.greenReviewOriginalText;
        delete offerHeading.dataset.greenReviewOriginalText;
      }
      upsell.querySelector(':scope > .green-review-created-heading')?.remove();
      upsell.querySelector(':scope > .green-review-dots')?.remove();
      upsell.classList.remove('green-review-upsells');
    });
  }

  function decorate(event) {
    scheduled = false;
    if (decorating || !activeContext()) {
      if (!activeContext()) deactivate();
      return;
    }
    decorating = true;
    try {
      html.classList.add('green-balm-review-active');
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      html.classList.add('green-balm-review-cart');
      drawer.querySelectorAll('.cart-item[data-variant-id]').forEach((row) => row.classList.add('green-review-cart-item'));
      updateNativeCopy(drawer);
      moveBadgesBelowDetails(drawer);
      carouselUpsells(drawer);
      syncOfferPresentation(drawer, event);
      animateRows(drawer);
    } finally {
      decorating = false;
    }
  }

  function scheduleDecorate() {
    if (scheduled || !activeContext()) return;
    scheduled = true;
    window.requestAnimationFrame(decorate);
  }

  function activate() {
    if (!activeContext()) return;
    html.classList.add('green-balm-review-active');
    scheduleDecorate();
  }

  function deactivate() {
    scheduled = false;
    document.querySelectorAll('.green-review-announcement, .green-review-item-offer, .green-review-free-card, .green-review-offer-eligibility').forEach((node) => node.remove());
    restoreBadges();
    restoreUpsells();
    restoreNativeCopy();
    document.querySelectorAll('.green-review-cart-item').forEach((row) => row.classList.remove('green-review-cart-item'));
    document.querySelectorAll('.green-review-green-item').forEach((row) => row.classList.remove('green-review-green-item'));
    document.querySelectorAll('.green-review-stripped, .green-review-saved').forEach((row) => row.classList.remove('green-review-stripped', 'green-review-saved'));
    html.classList.remove('green-balm-review-active', 'green-balm-review-cart', 'green-balm-review-one', 'green-balm-review-complete');
    rowPositions.clear();
    lastScroller = null;
    cartScrollTop = 0;
    if (status) status.hidden = true;
  }

  function reconcileContext() {
    if (activeContext()) activate();
    else deactivate();
  }

  const reviewDrawer = document.querySelector('cart-drawer');
  const refreshNativePresentation = (event) => {
    if (!activeContext()) {
      deactivate();
      return;
    }
    decorate(event);
  };
  document.addEventListener('cart-drawer:render', refreshNativePresentation);
  document.addEventListener('cart-drawer:update', refreshNativePresentation);
  reviewDrawer?.addEventListener('scroll', (event) => {
    if (event.target !== lastScroller) return;
    const difference = event.target.scrollTop - cartScrollTop;
    cartScrollTop = event.target.scrollTop;
    rowPositions.forEach((position) => { position.top -= difference; });
  }, true);
  window.addEventListener('pageshow', reconcileContext);
  window.addEventListener('pagehide', deactivate);
  window.addEventListener('popstate', reconcileContext);
  window.addEventListener('hashchange', reconcileContext);
  document.addEventListener('visibilitychange', reconcileContext);
  activate();
})();
